import { useState } from 'react';
import type { FormField } from './FormBuilder';

interface FormPreviewProps {
  fields: FormField[];
}

type FormValues = Record<string, string | boolean | string[]>;

export function FormPreview({ fields }: FormPreviewProps) {
  const [formValues, setFormValues] = useState<FormValues>({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (fieldName: string, value: string | boolean | string[]) => {
    const field = fields.find(f => f.name === fieldName);
    const actualFieldId = field?.fieldId || fieldName;
    
    setFormValues(prev => ({
      ...prev,
      [actualFieldId]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateField = (field: FormField, value: any): string | null => {
    const validation = field.validation || {};
    
    // Check required
    if (field.required) {
      if (!value || (typeof value === 'string' && value.trim() === '') || 
          (Array.isArray(value) && value.length === 0)) {
        return `${field.label} is required`;
      }
    }

    // Skip other validations if field is empty and not required
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return null;
    }

    const stringValue = String(value);

    // Length validation for text fields
    if ((field.type === 'text' || field.type === 'textarea') && typeof value === 'string') {
      if (validation.minLength && value.length < validation.minLength) {
        return `${field.label} must be at least ${validation.minLength} characters`;
      }
      if (validation.maxLength && value.length > validation.maxLength) {
        return `${field.label} must be no more than ${validation.maxLength} characters`;
      }
    }

    // Number range validation
    if (field.type === 'number') {
      const numValue = parseFloat(stringValue);
      if (isNaN(numValue)) {
        return `${field.label} must be a valid number`;
      }
      if (validation.min !== undefined && numValue < validation.min) {
        return `${field.label} must be at least ${validation.min}`;
      }
      if (validation.max !== undefined && numValue > validation.max) {
        return `${field.label} must be no more than ${validation.max}`;
      }
    }

    // Email validation
    if (field.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        return `${field.label} must be a valid email address`;
      }
    }

    // Pattern validation
    if (validation.pattern && (field.type === 'text' || field.type === 'textarea')) {
      try {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(stringValue)) {
          return `${field.label} format is invalid`;
        }
      } catch (e) {
        console.warn('Invalid regex pattern:', validation.pattern);
      }
    }

    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    fields.forEach(field => {
      const value = formValues[field.fieldId];
      const error = validateField(field, value);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setSubmitted(true);
    }
  };

  const handleReset = () => {
    setFormValues({});
    setSubmitted(false);
    setErrors({});
  };

  const renderField = (field: FormField) => {
    const value = formValues[field.fieldId] || '';
    const hasError = !!errors[field.name];
    const validation = field.validation || {};
    
    const baseClasses = `w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500'
    }`;

    // Add HTML validation attributes
    const validationProps: any = {};
    if (field.required) validationProps.required = true;
    if (validation.minLength) validationProps.minLength = validation.minLength;
    if (validation.maxLength) validationProps.maxLength = validation.maxLength;
    if (validation.min !== undefined) validationProps.min = validation.min;
    if (validation.max !== undefined) validationProps.max = validation.max;
    if (validation.pattern) validationProps.pattern = validation.pattern;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <input
            type={field.type}
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={baseClasses}
            {...validationProps}
          />
        );

      case 'textarea':
        return (
          <textarea
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={baseClasses}
            rows={3}
            {...validationProps}
          />
        );

      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={baseClasses}
            required={field.required}
          >
            <option value="">Select an option</option>
            {field.options && field.options.length > 0 ? (
              field.options.map((option) => (
                <option key={typeof option === 'object' ? option.value : option} value={typeof option === 'object' ? option.value : option}>
                  {typeof option === 'object' ? option.label : option}
                </option>
              ))
            ) : null}
          </select>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options && field.options.length > 0 ? (
              field.options.map((option, index) => (
                <label key={index} className="flex gap-2 items-center p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={!!formValues[`${field.fieldId}_${typeof option === 'object' ? option.value : option}`]}
                    onChange={(e) => handleInputChange(`${field.name}_${typeof option === 'object' ? option.value : option}`, e.target.checked)}
                    required={field.required && index === 0}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{typeof option === 'object' ? option.label : option}</span>
                </label>
              ))
            ) : (
              <div className="text-sm italic text-gray-500 dark:text-gray-400">No options configured</div>
            )}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options && field.options.length > 0 ? (
              field.options.map((option) => (
                <label key={typeof option === 'object' ? option.value : option} className="flex gap-2 items-center p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    name={field.name}
                    value={typeof option === 'object' ? option.value : option}
                    checked={formValues[field.fieldId] === (typeof option === 'object' ? option.value : option)}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{typeof option === 'object' ? option.label : option}</span>
                </label>
              ))
            ) : (
              <div className="text-sm italic text-gray-500 dark:text-gray-400">No options configured</div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderValidationHints = (field: FormField) => {
    const validation = field.validation || {};
    const hints: string[] = [];

    if (validation.minLength || validation.maxLength) {
      if (validation.minLength && validation.maxLength) {
        hints.push(`${validation.minLength}-${validation.maxLength} characters`);
      } else if (validation.minLength) {
        hints.push(`At least ${validation.minLength} characters`);
      } else if (validation.maxLength) {
        hints.push(`Up to ${validation.maxLength} characters`);
      }
    }

    if (validation.min !== undefined || validation.max !== undefined) {
      if (validation.min !== undefined && validation.max !== undefined) {
        hints.push(`Value between ${validation.min} and ${validation.max}`);
      } else if (validation.min !== undefined) {
        hints.push(`Minimum value: ${validation.min}`);
      } else if (validation.max !== undefined) {
        hints.push(`Maximum value: ${validation.max}`);
      }
    }



    if (validation.pattern) {
      hints.push('Must match the required format');
    }

    if (hints.length === 0) return null;

    return (
      <p className="mt-1 text-xs text-gray-500">
        {hints.join(' • ')}
      </p>
    );
  };

  if (fields.length === 0) {
    return (
      <div className="p-4 mx-auto max-w-2xl">
        <div className="py-12 text-center text-gray-500">
          <p className="text-lg">No fields to preview</p>
          <p className="text-sm">Add fields to see how your form will look and behave</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="p-4 mx-auto max-w-2xl">
        <div className="p-6 bg-white rounded-lg border shadow-sm">
          <div className="mb-6 text-center">
            <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">Form Submitted Successfully!</h3>
            <p className="mb-6 text-gray-600">All validation rules passed. Here's what was submitted:</p>
          </div>

          <div className="p-4 mb-6 bg-gray-50 rounded-lg">
            <h4 className="mb-3 font-medium text-gray-900">Form Data:</h4>
            <div className="space-y-2">
              {fields.map(field => {
                if (field.type === 'checkbox') {
                  // Handle checkbox fields specially
                  const checkboxValues: string[] = [];
                  const actualFieldId = field.fieldId;
                  if (field.options) {
                    field.options.forEach(option => {
                      const optionValue = typeof option === 'object' ? option.value : option;
                      const optionLabel = typeof option === 'object' ? option.label : option;
                      const key = `${actualFieldId}_${optionValue}`;
                      if (formValues[key]) {
                        checkboxValues.push(optionLabel);
                      }
                    });
                  }
                  return (
                    <div key={field.name} className="flex justify-between py-1">
                      <span className="text-sm font-medium text-gray-600">{field.label}:</span>
                      <span className="text-sm text-gray-900">
                        {checkboxValues.length > 0 ? checkboxValues.join(', ') : '(none selected)'}
                      </span>
                    </div>
                  );
                } else {
                  // Handle other field types
                  const actualFieldId = field.fieldId;
                  const value = formValues[actualFieldId];
                  return (
                    <div key={field.name} className="flex justify-between py-1">
                      <span className="text-sm font-medium text-gray-600">{field.label}:</span>
                      <span className="text-sm text-gray-900">
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                         Array.isArray(value) ? value.join(', ') : 
                         value || '(empty)'}
                      </span>
                    </div>
                  );
                }
              })}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
            >
              Test Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 mx-auto max-w-2xl">
      <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm dark:bg-gray-800 dark:border-gray-700">
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">Interactive Form Preview</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Test your form by filling it out. This preview includes all validation rules you've configured.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {fields.map((field) => (
            <div key={field.name}>
              {(field.type === 'checkbox' || field.type === 'select' || field.type === 'radio') ? (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {field.label}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  {renderField(field)}
                  {renderValidationHints(field)}
                  {errors[field.name] && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[field.name]}</p>
                  )}
                </div>
              ) : (
                <>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {field.label}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  {renderField(field)}
                  {renderValidationHints(field)}
                  {errors[field.name] && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[field.name]}</p>
                  )}
                </>
              )}
            </div>
          ))}
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
            >
              Submit Form
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md transition-colors dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 